<template>
  <SignupMainContainer
    :title="deadLink ? deadLink.title : 'Set Password'"
    :image-position="'left'"
    :max-width="'md:max-w-xl'"
    @form-submitted="formSubmitted"
  >
    <template #body>
      <p v-if="linkStatus === 'checking'" class="text-gray-600 text-sm tracking-tighter">Checking your link</p>
      <div v-else-if="deadLink">
        <h2 class="text-gray-800 text-xl tracking-tighter">{{ deadLink.heading }}</h2>
        <p role="status" class="text-gray-600 text-sm tracking-tighter mt-2">
          {{ newLinkSent ? NEW_LINK_SENT : deadLink.text }}
        </p>
        <div v-if="error" role="alert" class="text-red-600 text-sm mt-4">{{ error }}</div>
      </div>
      <template v-else>
        <h2 class="text-gray-800 text-xl tracking-tighter">Enter your new password.</h2>
        <div>
          <div>
            <label for="password" class="block text-gray-700 text-sm font-medium leading-5 tracking-tighter mt-4">
              Password
            </label>
            <div class="mt-1 rounded-md shadow-sm">
              <PasswordInput
                id="password"
                v-model="data.password"
                required=""
                autocomplete="new-password"
                placeholder="Enter your password"
              />
            </div>
          </div>
          <div>
            <label
              for="password_confirmation"
              class="block text-gray-700 text-sm font-medium leading-5 tracking-tighter mt-4"
            >
              Confirm Password
            </label>
            <div class="mt-1 rounded-md shadow-sm">
              <PasswordInput
                id="password_confirmation"
                v-model="data.password_confirmation"
                required=""
                autocomplete="new-password"
                :class="[passwordMismatch && data.password_confirmation ? 'border-red-400' : 'border-gray-200']"
                placeholder="Confirm your password"
              />
            </div>
            <div v-if="passwordMismatch && data.password_confirmation" class="text-red-600 text-sm mt-1">
              Passwords do not match
            </div>
          </div>
          <div v-if="error" role="alert" class="text-red-600 text-sm mt-4">{{ error }}</div>
          <TermsAgreement action="setting your password" class="text-sm text-gray-600 tracking-tighter mt-4" />
        </div>
      </template>
    </template>
    <template #footer>
      <div class="w-full flex flex-col">
        <div class="w-full flex justify-between">
          <button
            type="button"
            class="text-sm inline-flex items-center justify-center border-gray-800 border-2 text-md hover:shadow-lg transition-all rounded-lg px-3 md:px-8 py-3.5 leading-tight bg-white text-gray-800 tracking-tighter"
            @click="loginClicked"
          >
            <span class="hidden md:block">
              <span class="font-bold">Log in</span>
            </span>
            <span class="block md:hidden">
              <span class="font-bold">Log in</span>
            </span>
          </button>
          <button
            v-if="deadLink && !newLinkSent"
            type="button"
            data-test="new-link"
            :disabled="sendingNewLink"
            class="ml-2 inline-flex items-center border-gray-800 border-2 font-bold text-md transition-all rounded-full px-6 py-3.5 leading-tight bg-yellow-200 text-gray-800 hover:shadow-lg disabled:opacity-60"
            @click="requestNewLink"
          >
            <span class="mr-4">{{ deadLink.action }}</span>
            <SwooshArrow />
          </button>
          <button
            v-else-if="linkStatus === 'valid'"
            type="submit"
            :disabled="!isFormValid"
            :class="[
              isFormValid
                ? 'bg-yellow-200 text-gray-800 hover:shadow-lg'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed',
              'ml-2 inline-flex items-center border-gray-800 border-2 font-bold text-md transition-all rounded-full px-6 py-3.5 leading-tight'
            ]"
          >
            <span class="ml-auto transform -translate-y-px mr-4">Set password</span>
            <span class="mr-auto">
              <svg xmlns="http://www.w3.org/2000/svg" width="34" height="13" viewBox="0 0 34 13">
                <path
                  d="M3.11,6.82A20,20,0,0,1,1.2,6a.46.46,0,0,0-.67.15A2.32,2.32,0,0,0,0,7.25C0,7.64,0,7.84.3,8c.59.25,1.18.52,1.78.72"
                  transform="translate(0 0)"
                ></path>
                <path
                  d="M1.83,8.58A23.56,23.56,0,0,0,7.36,10a36.41,36.41,0,0,0,8.79,0A53.55,53.55,0,0,0,29.69,6.72l.19-.07,0,0c-.33.4-.65.8-1,1.19-.72.87-1.45,1.73-2.16,2.61a1.75,1.75,0,0,0,0,2.28.68.68,0,0,0,.52.22.73.73,0,0,0,.5-.26c.67-.78,1.33-1.58,2-2.39l3.65-4.41A2.13,2.13,0,0,0,34,4.5,1.24,1.24,0,0,0,33.91,4a.87.87,0,0,0-.28-.36,1.55,1.55,0,0,0-.32-.2L26.59.11c-.65-.31-1.25.05-1.54.92a1.22,1.22,0,0,0,.4,1.5c.95.49,1.91,1,2.86,1.42l1.78.89-.19.08A55.6,55.6,0,0,1,17.44,8a36.08,36.08,0,0,1-8.87.12A21.78,21.78,0,0,1,2.82,6.7c-.3-.12-.73-.1-1-.25"
                  fill="#000000"
                  transform="translate(0 0)"
                ></path>
              </svg>
            </span>
          </button>
        </div>
      </div>
    </template>
  </SignupMainContainer>
</template>
<script>
import { computed, defineComponent, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import SwooshArrow from '@/components/catering/SwooshArrow.vue'
import TermsAgreement from '@/components/TermsAgreement.vue'
import { TERMS_ACCEPTED_PARAMS } from '@/config/legal'
import { logInWithToken } from '@/helpers/tokenLogin'

const RESET_LINK_ERROR = 'This link has expired or was already used. Request a new one from the login page.'
const NETWORK_ERROR = 'Something went wrong. Please try again.'
const NEW_LINK_SENT = "We've emailed you a new link. It works for 24 hours."

const EMAIL_ME_A_NEW_LINK = 'Email me a new link'

const DEAD_LINKS = {
  expired: {
    title: 'Link expired',
    heading: 'This link has expired.',
    text: 'Password links work for 24 hours. We can email you a new one.',
    action: EMAIL_ME_A_NEW_LINK
  },
  used: {
    title: 'Link already used',
    heading: 'This link has already been used.',
    text: 'It was used to set a password, or a newer link was sent after it. We can email you a new one.',
    action: EMAIL_ME_A_NEW_LINK
  },
  invalid: {
    title: 'Link not valid',
    heading: "This link isn't valid.",
    text: 'Make sure you opened the whole link from the email, or request a new one.',
    action: 'Request a new link'
  }
}

const callResetApi = (path, options = {}) =>
  fetch(`${process.env.VUE_APP_API_SERVER}/${path}`, {
    ...options,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  }).then(async response => ({ response, body: await response.json().catch(() => ({})) }))

export default defineComponent({
  components: { SwooshArrow, TermsAgreement },
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    const error = ref('')
    const linkStatus = ref('checking')
    const newLinkSent = ref(false)
    const sendingNewLink = ref(false)
    const data = reactive({
      password: '',
      password_confirmation: ''
    })

    const resetToken = () => route.query.token || ''
    const encodedToken = () => encodeURIComponent(resetToken())

    const deadLink = computed(() => DEAD_LINKS[linkStatus.value] || null)

    const passwordMismatch = computed(() => {
      return data.password && data.password_confirmation && data.password !== data.password_confirmation
    })

    const isFormValid = computed(() => {
      return data.password && data.password_confirmation && !passwordMismatch.value
    })

    const checkLink = () => {
      if (!resetToken()) {
        linkStatus.value = 'invalid'
        return
      }

      callResetApi(`system_users_password_reset/status?token=${encodedToken()}`)
        .then(({ response, body }) => {
          linkStatus.value = (response.ok && body?.link_status) || 'valid'
        })
        .catch(() => {
          linkStatus.value = 'valid'
        })
    }

    const formSubmitted = () => {
      if (linkStatus.value !== 'valid' || !isFormValid.value) return
      error.value = ''

      callResetApi(`system_users_password_reset?token=${encodedToken()}`, {
        method: 'POST',
        body: JSON.stringify({
          ...TERMS_ACCEPTED_PARAMS,
          system_user: {
            password: data.password
          }
        })
      })
        .then(async ({ response, body }) => {
          if (!response.ok) {
            if (DEAD_LINKS[body?.link_status]) {
              linkStatus.value = body.link_status
              return
            }
            error.value = body?.errors?.[0] || RESET_LINK_ERROR
            return
          }
          const loggedIn = body?.token && (await logInWithToken(body.token, { store, router }).catch(() => false))
          if (!loggedIn) router.push('/login?reset-success')
        })
        .catch(() => {
          error.value = NETWORK_ERROR
        })
    }

    const requestNewLink = () => {
      if (linkStatus.value === 'invalid') {
        router.push('/forgot-password-email')
        return
      }

      error.value = ''
      sendingNewLink.value = true
      callResetApi('system_users_password_reset_request', {
        method: 'POST',
        body: JSON.stringify({ token: resetToken() })
      })
        .then(({ response }) => {
          if (response.ok) newLinkSent.value = true
          else error.value = NETWORK_ERROR
        })
        .catch(() => {
          error.value = NETWORK_ERROR
        })
        .finally(() => {
          sendingNewLink.value = false
        })
    }

    const loginClicked = () => {
      router.push('/login')
    }

    onMounted(checkLink)

    return {
      formSubmitted,
      data,
      error,
      linkStatus,
      deadLink,
      newLinkSent,
      sendingNewLink,
      requestNewLink,
      loginClicked,
      passwordMismatch,
      isFormValid,
      NEW_LINK_SENT
    }
  }
})
</script>
